import React from 'react'
import PropTypes from 'prop-types'
import { themeGet } from 'styled-system'
import { fontWeight } from 'uikit/styles/theme'
import View from 'helpers/View'
import ViewStack from 'components/ViewStack'
import Icon from 'uikit/atoms/Icon'
import { H4 } from 'uikit/helpers/Headings'
import styled from 'styled-components'

export const ConversationHeader = ({ icon, children }) => (
  <View alignItems="center" lineHeight={1} mb={3}>
    {icon && <Icon icon="lock" color={themeGet('gray')} size="18px" />}
    <View ml="8px" fontWeight={fontWeight.bold}>
      {children}
    </View>
  </View>
)

ConversationHeader.propTypes = {
  icon: PropTypes.string,
  children: PropTypes.node.isRequired,
}

export const DayDivider = ({ children }) => (
  <View justifyContent="center" color={themeGet('gray')} fontSize="14px" mb="15px">
    {children}
  </View>
)

DayDivider.propTypes = {
  children: PropTypes.node.isRequired,
}

const primaryTheme = {
  incomingMessage: {
    background: themeGet('wildSand'),
    color: themeGet('doveGray'),
    borderColor: themeGet('gallery'),
  },
  outgoingMessage: {
    background: themeGet('stTropaz'),
    color: themeGet('white'),
    borderColor: themeGet('stTropaz'),
  },
  chatWindow: {
    background: themeGet('white'),
  },
}

const ChatContext = React.createContext({
  theme: primaryTheme,
})

const TextContent = styled(View)`
  a {
    color: ${props => props.color};
    text-decoration: underline;
  }
`

export const Message = ({
  isIncoming = false,
  senderName,
  senderAvatar,
  status,
  text,
  images,
  files,
}) => {
  const { theme } = React.useContext(ChatContext)
  const { background, color, borderColor } = isIncoming
    ? theme.incomingMessage
    : theme.outgoingMessage
  return (
    <View alignItems={isIncoming ? 'flex-start' : 'flex-end'} mb="4px" flexDirection="column">
      <View flexDirection="row" maxWidth="400px">
        <View flexShrink="0"> {isIncoming && (senderAvatar || <View pr="50px" />)} </View>
        <ViewStack
          flexDirection="column"
          alignItems={isIncoming ? 'flex-start' : 'flex-end'}
          space="4px"
          flexBasis="0%"
          flexShrink="1"
          flexGrow="1"
        >
          {isIncoming && senderName && (
            <H4 style={{ marginBottom: '6px' }} fontSize="14px" lineHeight="1em">
              {senderName}
            </H4>
          )}
          {text && (
            <TextContent
              style={{ wordBreak: 'break-word', whiteSpace: 'pre-line' }}
              bg={background}
              color={color}
              p="10px 12px"
              borderRadius="4px"
              fontSize="14px"
              lineHeight={1.5}
              border="1px solid"
              borderColor={borderColor}
            >
              {text}
            </TextContent>
          )}
          {images}
          {files}
          {status && (
            <View
              alignSelf={isIncoming ? 'flex-start' : 'flex-end'}
              color="gray"
              fontSize="12px"
              textAlign="right"
              lineHeight={1}
              mt="4px"
            >
              {status}
            </View>
          )}
        </ViewStack>
      </View>
    </View>
  )
}

Message.propTypes = {
  isIncoming: PropTypes.bool,
  senderName: PropTypes.element,
  senderAvatar: PropTypes.element,
  status: PropTypes.string,
  text: PropTypes.node,
  images: PropTypes.node,
  files: PropTypes.node,
}

export const ChatWindow = ({ theme = primaryTheme, children }) => {
  return (
    <ChatContext.Provider value={{ theme }}>
      <View
        bg={theme.chatWindow.background}
        flexDirection="column"
        py="15px"
        px={['15px', '15px', '0']}
      >
        {children}
      </View>
    </ChatContext.Provider>
  )
}

ChatWindow.propTypes = {
  theme: PropTypes.object,
  children: PropTypes.any,
}
